import { useMemo } from 'react';
import { EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { ScheduleRequestEventData } from '@frontend/schedule-helpers';
import { theme } from '@frontend/theme';
import { Text, Truncated } from '@frontend/design-system';
import { PRIMARY_EVENT_COLOR } from '../../../../constants';
import { EventCardComponent } from '../../../schedule-calendar-components/event-card';
import { OverlappingEventIndicatorVariantType } from '../../calendar-view-v3/overlapping-event-indicator';
import { getDurationFromHours, checkIsNonAttendedEventStatus } from '../../utils';
import { GridEventCard } from '../atoms/grid-event-card';

type GridAppointmentCardProps = {
  id: string;
  title: string;
  startDate: string;
  startHour: string;
  endHour: string;
  locationName?: string;
  locationId?: string;
  entityId?: string;
  entityName?: string;
  personStatus?: string;
  eventStatus?: EventStatus;
  eventTypeColor?: string;
  backgroundColor?: string;
  overlappingIndicatorVariant?: OverlappingEventIndicatorVariantType;
  isYesterdayEvent?: boolean;
  scheduleRequestEventData?: ScheduleRequestEventData;
};

//#region Card

export const GridAppointmentCard = ({
  id,
  title,
  startDate,
  startHour,
  endHour,
  locationName = '',
  locationId,
  entityId,
  entityName,
  personStatus,
  eventStatus,
  eventTypeColor,
  backgroundColor = theme.colors.white,
  overlappingIndicatorVariant,
  isYesterdayEvent,
  scheduleRequestEventData,
}: GridAppointmentCardProps) => {
  const duration = useMemo(() => getDurationFromHours(startHour, endHour), [startHour, endHour]);
  const primaryColor = eventTypeColor ?? PRIMARY_EVENT_COLOR;
  const isNonAttendedEvent = checkIsNonAttendedEventStatus(eventStatus);

  return (
    <GridEventCard
      id={id}
      primaryColor={primaryColor}
      backgroundColor={backgroundColor}
      isNonAttendedEvent={isNonAttendedEvent}
    >
      <GridEventCard.ShellWrapper>
        <GridEventCard.NonAttendedEventStatusIcon eventStatus={eventStatus} />
        <GridEventCard.Shell>
          <GridEventCard.PersonStatusBar personStatus={personStatus} />
          <GridEventCard.EventColoredLine />
          <EventCardComponent
            id={id}
            eventType='appointment'
            title={title}
            duration={duration}
            locationName={locationName}
            locationId={locationId}
            providerId={entityId}
            providerName={entityName}
            isExpanded={false}
            startDate={startDate}
            overlappingIndicatorVariant={overlappingIndicatorVariant}
            isYesterdayEvent={isYesterdayEvent}
            scheduleRequestEventData={scheduleRequestEventData}
          />
        </GridEventCard.Shell>
      </GridEventCard.ShellWrapper>
    </GridEventCard>
  );
};

//#endregion

//#region Tooltip

type GridAppointmentTooltipProps = {
  title: string;
  startHour: string;
  endHour: string;
  appointmentType?: string;
};

export const GridAppointmentTooltip = ({ title, startHour, endHour, appointmentType }: GridAppointmentTooltipProps) => {
  const duration = useMemo(() => getDurationFromHours(startHour, endHour), [startHour, endHour]);

  return (
    <div css={appointmentTooltipStyle}>
      <Truncated as={Text} weight='bold' size='medium' color='white'>
        {title}
      </Truncated>
      {!!appointmentType && (
        <Truncated as={Text} size='small' color='white'>
          {appointmentType}
        </Truncated>
      )}
      <Truncated as={Text} size='small' color='white'>
        {duration}
      </Truncated>
    </div>
  );
};

const appointmentTooltipStyle = {
  display: 'grid',
  gap: theme.spacing(0.25),
  maxWidth: '200px',
};

//#endregion
